import { useNavigate, useOutletContext, useParams } from 'react-router-dom';
import styles from './index.module.scss';
import { Flex, Tooltip } from 'antd';
import AccionaButton from '../../../../../components/AccionaButton';
import { useIntl } from 'react-intl';
import AccionaTable from '../../../../../components/AccionaTable';
import AccionaTableButton from '../../../../../components/AccionaTable/AccionaTableButton';
import { useCallback, useContext, useEffect, useState } from 'react';
import { CheckCircleFilled, WarningFilled } from '@ant-design/icons';
import { useDispatch, useSelector } from 'react-redux';
import PropTypes from 'prop-types';
import { generateConcreteClassPDF, generateLG14PDF } from './utils.index';
import Modal from '../../../../../components/Modal';
import { NotificationsContext } from '../../../../../context/NotificationsContext';

const { actionsWrapper } = styles;

const CustomTypeControlList = ({
  columns,
  title,
  entity,
  action,
  onView,
  customDataParseFunction,
}) => {
  const navigate = useNavigate();
  const { project } = useOutletContext();
  const { id } = useParams();
  const intl = useIntl();
  const dispatch = useDispatch();
  const { list } = useSelector((state) => state[entity]);
  const { pushNotification } = useContext(NotificationsContext);
  const [loadingConcreteClassPDF, setLoadingConcreteClassPDF] = useState(false);
  const [loadingLG14PDF, setLoadingLG14PDF] = useState(false);
  const [showSelectReportModeModal, setShowSelectReportModeModal] =
    useState(false);
  const [selectedConcreteClass, setSelectedConcreteClass] = useState(null);

  const actions = useCallback(
    (item) => {
      return (
        <>
          <AccionaTableButton
            type="view"
            toolTip={intl.formatMessage({
              id: 'pages.quality.normative.customTypeControlList.list.view.tooltip',
            })}
            disabled={item.concrete_batch_data.length === 0}
            onClick={() => {
              const totalLots = item.concrete_batch_data.length;
              localStorage.setItem('Clase', item.rawObject.name);
              if (totalLots > 0) {
                onView(item.rawObject);
              }
            }}
          />
          <AccionaTableButton
            type="details"
            toolTip={intl.formatMessage({
              id: 'pages.quality.normative.customTypeControlList.list.downloadPDF.tooltip',
            })}
            loading={loadingConcreteClassPDF.name === item.rawObject.name}
            onClick={() => {
              if (entity === 'concreteClassesStatistical') {
                setSelectedConcreteClass(item.rawObject);
                setShowSelectReportModeModal(true);
              } else {
                setLoadingConcreteClassPDF(item.rawObject);
                generateConcreteClassPDF(
                  {
                    concreteClass: {
                      ...item.rawObject,
                      control_type: 100,
                    },
                    project,
                  },
                  false,
                  100,
                )
                  .then(() => {
                    setLoadingConcreteClassPDF(false);
                  })
                  .catch(() => {
                    setLoadingConcreteClassPDF(false);
                    pushNotification({
                      type: 'error',
                      message: intl.formatMessage({
                        id: 'pages.quality.normative.customTypeControlList.list.downloadPDF.error',
                      }),
                    });
                  });
              }
            }}
          />
        </>
      );
    },
    [
      entity,
      intl,
      loadingConcreteClassPDF.name,
      onView,
      project,
      pushNotification,
    ],
  );

  useEffect(() => {
    dispatch(action(id));
  }, [action, dispatch, id]);

  return (
    <>
      <div className={`row ${actionsWrapper}`}>
        <div className="col-sm-12">
          <div className={`row ${actionsWrapper}`}>
            <div className="col-sm-12">
              <Flex justify="flex-end">
                {list.length > 0 && (
                  <AccionaButton
                    loading={loadingLG14PDF}
                    style={{ padding: 15 }}
                    id="export-traceability-pdf"
                    type="white"
                    msg="pdf"
                    onClick={() => {
                      setLoadingLG14PDF(true);
                      generateLG14PDF({
                        type:
                          entity === 'concreteClassesStatistical'
                            ? 'statistical'
                            : '100',
                        project,
                      }).then(() => {
                        setLoadingLG14PDF(false);
                      });
                    }}
                    classIcon="icon fas fa-download"
                    tooltip={intl.formatMessage({
                      id: 'pages.quality.normative.customTypeControlList.list.downloadPDFLG14.tooltip',
                    })}
                  />
                )}
                <AccionaButton
                  id="traceability"
                  style={{ padding: 15 }}
                  type="primary"
                  onClick={() => {
                    navigate('../traceability');
                  }}
                  msg={intl.formatMessage({ id: 'button.traceability' })}
                  classIcon="icon fas fa-route"
                />
                {entity !== 'concreteClasses100' && (
                  <AccionaButton
                    style={{ padding: 15 }}
                    id="100%Control"
                    type="primary"
                    onClick={() => {
                      navigate(`../100-control`);
                    }}
                    msg={intl.formatMessage({ id: 'button.100%Control' })}
                    classIcon="icon fas fa-percentage"
                  />
                )}
                {entity === 'concreteClasses100' && (
                  <AccionaButton
                    style={{ padding: 15 }}
                    id="statisticalControl"
                    type="primary"
                    onClick={() => {
                      navigate(`../statistical-control`);
                    }}
                    msg={intl.formatMessage({
                      id: 'button.statisticalControl',
                    })}
                    classIcon="icon fas fa-chart-bar"
                  />
                )}
              </Flex>
            </div>
          </div>
        </div>
      </div>
      <div className="row">
        <div className="col-sm-12">
          <AccionaTable
            pageSize={1000}
            actionsColumnWidth={100}
            hideSelectAllCheckbox
            isSelectionDisabled={() => true}
            rowHeight="40px"
            title={title}
            columns={columns}
            entity={entity}
            getItems={() => {}}
            actions={actions}
            dataParseFunction={(items) => {
              let parsedItems = [...items];

              if (customDataParseFunction) {
                parsedItems = customDataParseFunction(items);
              }
              return parsedItems.map((item) => ({
                ...item,
                rawObject: { ...item },
                name: (
                  <p style={{ paddingLeft: '1em', marginBottom: 0 }}>
                    {item.name}{' '}
                    {item.concrete_batch_data.length === 0 && (
                      <Tooltip
                        title={intl.formatMessage({
                          id: 'pages.quality.normative.customTypeControlList.list.class.warrning.tooltip',
                        })}
                      >
                        <WarningFilled style={{ color: '#f9b628' }} />
                      </Tooltip>
                    )}
                  </p>
                ),
                concrete_quality: item.concrete_quality ? (
                  <CheckCircleFilled style={{ color: '#1376f7' }} />
                ) : null,
                certifiedDispersion: item.certified_dis ? (
                  <CheckCircleFilled style={{ color: '#f9b628' }} />
                ) : null,
              }));
            }}
          />
        </div>
      </div>
      <Modal
        width="40vw"
        open={showSelectReportModeModal}
        onCancel={() => {
          setShowSelectReportModeModal(false);
          setSelectedConcreteClass(null);
        }}
        closable
      >
        <div style={{ padding: '1em' }}>
          <p
            style={{
              fontSize: '1.2em',
              textAlign: 'center',
              width: '70%',
              margin: '0 auto',
              marginBottom: 20,
            }}
          >
            {intl.formatMessage({
              id: 'pages.quality.normative.customTypeControlList.list.selectReportMode.modal.description',
            })}
          </p>
          <div
            style={{
              marginTop: '10px',
              textAlign: 'center',
              display: 'flex',
              justifyContent: 'center',
              gap: '20px',
            }}
          >
            <AccionaButton
              type="primary"
              onClick={() => {
                const type =
                  entity === 'concreteClasses100' ? '100' : 'statistical';
                setLoadingConcreteClassPDF(selectedConcreteClass);
                generateConcreteClassPDF(
                  {
                    concreteClass: {
                      ...selectedConcreteClass,
                      control_type: type,
                    },
                    project,
                  },
                  false,
                  type,
                ).then(() => {
                  setLoadingConcreteClassPDF(false);
                });
                setShowSelectReportModeModal(false);
                setSelectedConcreteClass(null);
              }}
              msg={intl.formatMessage({
                id: 'pages.quality.normative.customTypeControlList.list.selectReportMode.modal.complete',
              })}
            ></AccionaButton>
            <AccionaButton
              type="default"
              onClick={() => {
                const type =
                  entity === 'concreteClasses100' ? '100' : 'statistical';
                setLoadingConcreteClassPDF(selectedConcreteClass);
                generateConcreteClassPDF(
                  {
                    concreteClass: {
                      ...selectedConcreteClass,
                      control_type: type,
                    },
                    project,
                  },
                  true,
                  type,
                )
                  .then(() => {
                    setLoadingConcreteClassPDF(false);
                  })
                  .catch(() => {
                    setLoadingConcreteClassPDF(false);
                    pushNotification({
                      type: 'error',
                      message: intl.formatMessage({
                        id: 'pages.quality.normative.customTypeControlList.list.downloadPDF.error',
                      }),
                    });
                  });
                setShowSelectReportModeModal(false);
                setSelectedConcreteClass(null);
              }}
              msg={intl.formatMessage({
                id: 'pages.quality.normative.customTypeControlList.list.selectReportMode.modal.onlyControlMash',
              })}
            ></AccionaButton>
          </div>
        </div>
      </Modal>
    </>
  );
};

CustomTypeControlList.propTypes = {
  columns: PropTypes.shape({}).isRequired,
  normative: PropTypes.oneOf(['coe', 'ehe']).isRequired,
  title: PropTypes.string.isRequired,
  entity: PropTypes.string.isRequired,
  action: PropTypes.func.isRequired,
  onView: PropTypes.func.isRequired,
  customDataParseFunction: PropTypes.func,
};

CustomTypeControlList.defaultProps = {
  customDataParseFunction: null,
};

export default CustomTypeControlList;
